import * as React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet, useLocation, useRoutes } from "react-router-dom";
import { isDev } from "config";
import { SignIn, useAuth } from "features/auth";
import {
  About,
  BecomeAReviewer,
  BecomeAModerator,
  DeiInitiatives,
  FAQ,
  ForCreators,
  PrivacyPolicy,
  TermsOfService,
} from "features/corporate";
import { LayoutFull, LayoutSite } from "features/layouts";
import { AllNotifications } from "features/notifications";
import { EditProfile, ProfilePage } from "features/profile";
import { AdminWrapper, UserSearch } from "features/admin";
import { MintStatusReport, QAWrapper } from "features/qa";
import { MobileSignIn } from "features/profile/components/MobileSignIn";
import { RedirectState } from "types";

import * as Pages from "pages";

const routes: RouteObject[] = [
  {
    element: <LayoutSite />,
    children: [
      { index: true, element: <Pages.HubHome /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/about", element: <About /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/terms-of-service", element: <TermsOfService /> },
      { path: "/for-creators", element: <ForCreators /> },
      { path: "/dei-initiatives", element: <DeiInitiatives /> },
      {
        element: <RequireAuth />,
        children: [
          { path: "/become-a-reviewer", element: <BecomeAReviewer /> },
          { path: "/become-a-moderator", element: <BecomeAModerator /> },
          { path: "/redeem-hug-points", element: <Pages.RewardsShop /> },
          {
            path: "admin",
            element: <AdminWrapper />,
            children: [
              { path: "reports", element: <Pages.CuratorReport /> },
              { path: "raffles", element: <Pages.RaffleReport /> },
              {
                path: "featured-projects",
                element: <Pages.AdminFeaturedProjects />,
              },
              { path: "users", element: <UserSearch /> },
              {
                path: "users/:userId",
                element: <Pages.AdminUserDetails />,
              },
            ],
          },
          {
            path: "qa",
            element: <QAWrapper />,
            children: [
              { path: "mint-status", element: <MintStatusReport /> },
              { path: "tickets", element: <Pages.AdminTicketList /> },
              {
                path: "tickets/:ticketId",
                element: <Pages.AdminTicketDetails />,
              },
            ],
          },
          {
            path: "moderation",
            element: <Pages.Moderation />,
          },
        ],
      },
      {
        path: "projects",
        children: [
          { path: "create", element: <Pages.CreateProject /> },
          { path: ":projectId", element: <Pages.ProjectDetails /> },
          { path: ":projectId/edit", element: <Pages.EditProject /> },
          {
            path: ":projectId/review",
            element: <Pages.CreateProjectReview />,
          },
          {
            path: ":projectId/reviews/:reviewedByUserId/edit",
            element: <Pages.EditProjectReview />,
          },
        ],
      },
      {
        path: "artists",
        children: [
          { path: "create", element: <Pages.CreateArtist /> },
          { path: ":artistId", element: <Pages.ArtistDetails /> },
          { path: ":artistId/edit", element: <Pages.EditArtist /> },
          {
            path: ":artistId/review",
            element: <Pages.CreateArtistReview />,
          },
          {
            path: ":artistId/reviews/:reviewedByUserId/edit",
            element: <Pages.EditArtistReview />,
          },
        ],
      },
      {
        path: "profile",
        element: <RequireAuth />,
        children: [
          { index: true, element: <ProfilePage /> },
          { path: "edit", element: <EditProfile /> },
          { path: "mobileSignIn", element: <MobileSignIn /> },
          { path: "notifications", element: <AllNotifications /> },
        ],
      },
      {
        children: [{ path: "search", element: <Pages.Search /> }],
      },
    ],
  },
  {
    element: <LayoutFull />,
    children: [
      // NOTE: this is helpful for testing components or CSS while developing
      ...(isDev ? [{ path: "/dev", element: <Pages.Dev /> }] : []),
      { path: "/apply", element: <Pages.Apply /> },
      { path: "/sign-in", element: <SignIn /> },
      {
        element: <RequireAuth />,
        children: [
          { path: "/mint", element: <Pages.Mint /> },
          { path: "/mint-admin", element: <Pages.MintAdmin /> },
        ],
      },
      // NOTE: IMPORTANT! Must appear last for wildcard to catch all unhandled routes
      { path: "*", element: <Pages.NotFound /> },
    ],
  },
];

/**
 * Top level wrapper to prevent access to child routes without connecting wallet
 */
function RequireAuth() {
  const { isSignedIn } = useAuth();
  const location = useLocation();

  if (!isSignedIn && location.pathname !== "/sign-in") {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

function Router() {
  // Scroll on all transitions
  const { hash, pathname } = useLocation();
  React.useLayoutEffect(() => {
    if (hash === "") {
      // No element id was provided, so scroll to top on every page transition
      window.scrollTo({ top: 0, left: 0 });
    } else {
      // TODO: this doesn't work for pages with loading states
      // see /projects/1989-sisters#reviews for example

      // Find the requested element, removing the leading "#"
      const element = document.getElementById(hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash, pathname]);

  return useRoutes(routes);
}

export type { RedirectState };
export { Router, routes };
